import { Tag } from "lucide-react";
import {
    HoverCard,
    HoverCardContent,
    HoverCardTrigger,
} from "@/components/ui/hover-card";
import { parseFileTags, buildTagMap } from "@/features/tags/utils/tag-utils";
import { useAtomValue, useSetAtom } from "jotai";
import { tagIdToKeyValueMapAtom, refetchTagsAtom } from "../state/tagAtoms";
import { useEffect, useCallback } from "react";

interface TagsHoverCardProps {
    fileTags: string;
}

export function TagsHoverCard({ fileTags }: TagsHoverCardProps) {
    const tagIdMap = useAtomValue(tagIdToKeyValueMapAtom);
    const refetchTags = useSetAtom(refetchTagsAtom);
    const tagParts = parseFileTags(fileTags, tagIdMap);

    const hasUnresolvedTags = fileTags && tagParts.length === 0;

    const handleRefetch = useCallback(() => {
        console.log(
            "[TagsHoverCard] Unresolved tags detected, triggering refetch",
        );
        refetchTags();
    }, [refetchTags]);

    useEffect(() => {
        if (hasUnresolvedTags) {
            handleRefetch();
        }
    }, [hasUnresolvedTags, handleRefetch]);

    return (
        <HoverCard openDelay={200}>
            <HoverCardTrigger asChild>
                <Tag className="h-4 w-4 cursor-pointer" />
            </HoverCardTrigger>
            <HoverCardContent className="w-44 bg-md-surface-level-0 border-none shadow-lg">
                <div className="space-y-3">
                    {Object.entries(buildTagMap(tagParts)).map(
                        ([key, values]) => (
                            <div key={key} className="space-y-1">
                                <div className="text-xs font-semibold text-md-on-surface-variant uppercase tracking-wide">
                                    {key}
                                </div>
                                <div className="pl-3 space-y-0.5">
                                    {values.map((value, index) => (
                                        <div
                                            key={index}
                                            className="text-sm text-md-on-surface flex items-center"
                                        >
                                            <div className="w-1.5 h-1.5 bg-md-outline rounded-full mr-2 flex-shrink-0"></div>
                                            {value}
                                        </div>
                                    ))}
                                </div>
                            </div>
                        ),
                    )}
                </div>
            </HoverCardContent>
        </HoverCard>
    );
}

export default TagsHoverCard;
